import { useEffect, useRef } from 'react';
import { Sparkles, User } from 'lucide-react';
import { useBrain } from '../../contexts/BrainContext';
import { ToolCallBubble } from './ToolCallBubble';

export function ChatStream() {
  const { messages, pending } = useBrain();
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, pending]);

  if (messages.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 px-6 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-500 to-purple-500 text-white shadow-lg shadow-brand-900/40">
          <Sparkles className="h-6 w-6" />
        </div>
        <div>
          <h3 className="text-base font-semibold text-neutral-100">Velocity 9 Brain</h3>
          <p className="mt-1 text-xs text-neutral-400">
            Ask anything about your deals, inspectors, suburbs, or calendar.
          </p>
        </div>
        <div className="mt-2 flex flex-wrap justify-center gap-2 text-[11px]">
          {[
            'Show me deals over $5k in Highfields',
            'Which inspectors are free this Friday?',
            'Summarise this week\'s callouts',
            'Where are most of our open jobs?',
          ].map((s) => (
            <span key={s} className="rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 text-neutral-300">
              {s}
            </span>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 px-4 py-4">
      {messages.map((m) => {
        if (m.role === 'user') {
          return (
            <div key={m.localId} className="flex justify-end gap-2">
              <div className="max-w-[85%] rounded-2xl rounded-br-md bg-brand-600 px-3.5 py-2 text-sm text-white">
                {m.text}
              </div>
              <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-white/10 text-neutral-300">
                <User className="h-3.5 w-3.5" />
              </div>
            </div>
          );
        }
        return (
          <div key={m.localId} className="flex justify-start gap-2">
            <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-purple-500 text-white">
              <Sparkles className="h-3.5 w-3.5" />
            </div>
            <div className="max-w-[85%]">
              {m.toolEvents.length > 0 && (
                <div className="mb-1 flex flex-col gap-1">
                  {m.toolEvents.map((te, i) => (
                    <ToolCallBubble
                      key={`${m.localId}-tool-${i}`}
                      name={te.name}
                      input={te.input}
                      ok={te.ok}
                      error={te.error}
                      ms={te.ms}
                    />
                  ))}
                </div>
              )}
              {(m.text || m.pending) && (
                <div className="rounded-2xl rounded-bl-md bg-white/[0.05] px-3.5 py-2 text-sm text-neutral-100">
                  {m.text && <p className="whitespace-pre-wrap leading-relaxed">{m.text}</p>}
                  {m.pending && !m.text && (
                    <span className="inline-flex gap-1">
                      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-brand-400" />
                      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-brand-400 [animation-delay:120ms]" />
                      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-brand-400 [animation-delay:240ms]" />
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
      <div ref={endRef} />
    </div>
  );
}
